<template>
  <v-card outlined> 
     <!-- Header com título -->
    <div class="d-flex justify-space-between align-center px-4 pt-1 pb-0">
      <div class="chart-title">
        Consumo
      </div>
      <div class="d-flex toggle-container">
        <Dropdown
          v-model="selectedConsumptionMode"
          :options="consumptionModeOptions"
          class="chart-type-select"
          style="min-width: 150px; max-width: 180px;"
        />
        <Dropdown
          v-model="selectedChartType"
          :options="chartTypeOptions"
          class="chart-type-select"
          style="min-width: 180px; max-width: 220px;"
        />
      </div>
    </div>

    <!-- Área do gráfico -->
    <v-card-text class="d-flex flex-grow-1 align-end pa-0 px-2" style="height: 100%;">
      <div class="chart-container">
        <canvas ref="waterUsageChart"></canvas>
      </div>
    </v-card-text>
    <div class="chart-date-info">
      <img :src="expandIcon" alt="expand icon" class="date-info-icon" />
      {{ chartDate }}
    </div>
  </v-card>
</template>

<script>
import Chart from 'chart.js'
import Dropdown from '@/components/DropMenus/Dropdown.vue'
import expandIcon from '@/assets/dashboard/LegendNode.png'
import { mapGetters } from 'vuex'

// Plugin para arredondar as barras
const roundedBarsPlugin = {
  id: 'roundedBars',
  afterDatasetsDraw: function (chart) {
    chart.data.datasets.forEach(function (dataset, datasetIndex) {
      if (chart.config.type === "bar" || (dataset.type === "bar")) {
        const meta = chart.getDatasetMeta(datasetIndex);
        meta.data.forEach(function (bar) {
          const ctx = chart.ctx;
          const model = bar._model;
          const yTop = model.y;
          const xLeft = model.x - model.width / 2;
          const xRight = model.x + model.width / 2;
          const yBottom = model.base;
          const radius = 10;

          const roundedCorners = yTop !== yBottom;

          ctx.clearRect(xLeft, yTop, xRight - xLeft, yBottom - yTop);

          ctx.save();
          ctx.beginPath();
          ctx.moveTo(xLeft, yBottom);

          if (roundedCorners) {
            ctx.lineTo(xLeft, yTop + radius);
            ctx.quadraticCurveTo(xLeft, yTop, xLeft + radius, yTop);
            ctx.lineTo(xRight - radius, yTop);
            ctx.quadraticCurveTo(xRight, yTop, xRight, yTop + radius);
          } else {
            ctx.lineTo(xLeft, yTop);
            ctx.lineTo(xRight, yTop);
          }
          ctx.lineTo(xRight, yBottom);
          ctx.closePath();

          ctx.fillStyle = model.backgroundColor;
          ctx.fill();
          ctx.restore();
        });
      }
    });
  },
};

// Plugin personalizado para desenhar a linha de meta
const targetLinePlugin = {
  id: 'targetLine',
  afterDraw: function(chart) {
    if (chart.config.options.targetValue !== undefined) {
      const ctx = chart.ctx;
      const yAxis = chart.scales['y-axis-0'];
      const targetValue = chart.config.options.targetValue;
      const yPosition = yAxis.getPixelForValue(targetValue);
      
      // Desenhar a linha
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(chart.chartArea.left, yPosition);
      ctx.lineTo(chart.chartArea.right, yPosition);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#002E71'; // Azul
      ctx.stroke();
      
      // Adicionar o label
      ctx.restore();
    }
  }
};


export default {
  name: 'WaterUsageChart',
  components: {
    Dropdown
  },
  props: {
    monthName: {
      type: String,
      default: 'Abril'
    },
    startDate: {
      type: String,
      default: ''
    },
    endDate: {
      type: String,
      default: ''
    },
    year: {
      type: String,
      default: '2025'
    },
    dailyData: {
      type: Array,
      default: () => [
        { date: '01/ABR', value: 32 },
        { date: '04/ABR', value: 95 },
        { date: '07/ABR', value: 28 },
        { date: '10/ABR', value: 66 },
        { date: '13/ABR', value: 25 },
        { date: '16/ABR', value: 52 },
        { date: '19/ABR', value: 18 },
        { date: '22/ABR', value: 73 },
        { date: '25/ABR', value: 35 },
        { date: '28/ABR', value: 94 },
        { date: '30/ABR', value: 1 }
      ]
    },
    weeklyData: {
      type: Array,
      default: () => [
        { date: 'Semana 1', value: 55 },
        { date: 'Semana 2', value: 48 },
        { date: 'Semana 3', value: 60 },
        { date: 'Semana 4', value: 44 }
      ]
    },
    monthlyData: {
      type: Array,
      default: () => [
        { date: 'Jan', value: 42 },
        { date: 'Fev', value: 53 },
        { date: 'Mar', value: 46 },
        { date: 'Abr', value: 55 },
        { date: 'Mai', value: 49 },
        { date: 'Jun', value: 52 }
      ]
    },
    targetValue: {
      type: Number,
      default: 60
    }
  },
  data() {
    return {
      selectedPeriod: 'daily',
      selectedChartType: 'line',
      selectedConsumptionMode: 'total',
      unitLabels: {
        money: 'R$',
        liters: 'Litros',
        cubic: 'm³'
      },
      periodLabels: {
        monthly: 'Mensal',
        weekly: 'Semanal',
        daily: 'Diário'
      },
      chart: null,
      chartTypeOptions: [
        { text: 'Gráfico de linha', value: 'line' },
        { text: 'Gráfico de barras', value: 'bar' }
      ],
      consumptionModeOptions: [
        { text: 'Total', value: 'total' },
        { text: 'Agua', value: 'water' },
        { text: 'Efluentes', value: 'sewer' }
      ],
      expandIcon
    }
  },
  computed: {
    ...mapGetters(['unitMeasure']),
    selectedUnit() {
      const unitMap = {
        m3: 'cubic',
        cubic: 'cubic',
        cubic_meter: 'cubic',
        cubic_meters: 'cubic',
        liters: 'liters',
        liter: 'liters',
        l: 'liters'
      };
      return unitMap[this.unitMeasure] || 'cubic';
    },
    chartDate() {
      // Se startDate estiver no formato DD/MM/AAAA
      if (this.startDate && this.startDate.length === 10) {
        const [day, month, year] = this.startDate.split('/');
        const monthNames = [
          'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
          'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
        ];
        const monthIndex = parseInt(month, 10) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          return `${monthNames[monthIndex]}/${year}`;
        }
      }
      // fallback para o formato antigo
      return `Dados não definidos`;
    },
    activeData() {
      switch(this.selectedPeriod) {
        case 'monthly':
          return this.monthlyData
        case 'weekly':
          return this.weeklyData
        case 'daily':
        default:
          return this.dailyData
      }
    },
    chartTargetValue() {
      return this.selectedUnit === 'liters' ? this.targetValue * 1000 : this.targetValue;
    },
    chartData() {
      const labels = this.activeData.map(item => item.date)
      const waterData = this.getSeriesValues('water')
      const sewerData = this.getSeriesValues('sewer')
      const totalData = this.getSeriesValues('total')
      const hasSplitSeries = this.activeData.some((item) =>
        item && (item.valueWater !== undefined || item.valueSewer !== undefined)
      )

      const datasetsByMode = {
        total: hasSplitSeries
          ? [
            this.createConsumptionDataset('Agua', waterData, '#4092FF', '#E0EFFF'),
            this.createConsumptionDataset('Efluentes', sewerData, '#F2B27B', '#FFF1E5')
          ]
          : [this.createConsumptionDataset('Total', totalData, '#4092FF', '#E0EFFF')],
        water: [this.createConsumptionDataset('Agua', waterData, '#4092FF', '#E0EFFF')],
        sewer: [this.createConsumptionDataset('Efluentes', sewerData, '#F2B27B', '#FFF1E5')]
      }

      return {
        labels,
        datasets: datasetsByMode[this.selectedConsumptionMode] || datasetsByMode.total
      }
    },
    chartOptions() {
      const self = this
      const shouldStack = this.selectedChartType === 'bar' && this.selectedConsumptionMode === 'total'
      return {
        responsive: true,
        maintainAspectRatio: false,
        targetValue: this.selectedConsumptionMode === 'total' ? this.chartTargetValue : undefined,
        legend: {
          display: this.chartData.datasets.length > 1,
          labels: {
            boxWidth: 12,
            fontColor: 'rgba(0, 0, 0, 0.70)',
            fontFamily: 'Manrope',
            fontSize: 12
          }
        },
        elements: {
          line: {
            tension: 0.4
          }
        },
        animation: {
          duration: 0 // Desativa animações para melhor performance no redimensionamento
        },
        onResize: function(chart, size) {
          // Força atualização do gráfico quando redimensionado
          chart.update('none');
        },
        scales: {
          yAxes: [{
            stacked: shouldStack,
            ticks: {
              beginAtZero: true,
              callback: function(value) {
                switch(self.selectedUnit) {
                  case 'cubic':
                    return `${value} m³`
                  case 'liters':
                  default:
                    return `${value} L`
                }
              },
              fontColor: 'rgba(0, 0, 0, 0.70)',
              fontSize: 12,
              fontFamily: 'Manrope',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal'
            },
            gridLines: {
              display:true,
              color: 'rgba(0, 0, 26, 0.15)',
              borderDash: [4, 5],
            }
          }],
          xAxes: [{  
            stacked: shouldStack,
            ticks: {
              fontColor: 'rgba(0, 0, 0, 0.70)',
              fontSize: 12,
              fontFamily: 'Manrope',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal'
            },
            gridLines: {
              display: true,
              color: 'rgba(0, 0, 26, 0.15)',
              borderDash: [4, 5],
              zeroLineColor:'rgba(0, 0, 26, 0.15)',
              zeroLineBorderDash:[4, 5],
            }
          }]
        },
        tooltips: {
          callbacks: {
            title: function(tooltipItems, data) {
              return tooltipItems[0].xLabel;
            },
            label: function(tooltipItem, data) {
              const value = tooltipItem.yLabel
              const dataset = data.datasets[tooltipItem.datasetIndex] || {}
              const label = dataset.label ? `${dataset.label}: ` : ''
              switch(self.selectedUnit) {
                case 'money':
                  return `${label}R$ ${Number(value).toFixed(2)}`;
                case 'cubic':
                  return `${label}${value} m³`;
                case 'liters':
                default:
                  return `${label}${value} L`;
              }
            }
          }
        }
      }
    }
  },
  methods: {
    createGradient() {
      if (!this.$refs.waterUsageChart) return 'rgba(25, 118, 210, 0.2)'
      
      const ctx = this.$refs.waterUsageChart.getContext('2d')
      const gradient = ctx.createLinearGradient(0, 0, 0, 120)
      gradient.addColorStop(0, 'rgba(29, 138, 255, 0.60)')
      gradient.addColorStop(1, 'rgba(29, 138, 255, 0.00)') 
      return gradient
    },
    normalizeValue(value) {
      const normalized = Number(value || 0)
      return this.selectedUnit === 'liters' ? normalized * 1000 : normalized
    },
    getSeriesValues(series) {
      return this.activeData.map((item) => {
        const water = Number(item && item.valueWater !== undefined ? item.valueWater : item?.value || 0)
        const sewer = Number(item && item.valueSewer !== undefined ? item.valueSewer : 0)
        const total = Number(item && item.valueTotal !== undefined ? item.valueTotal : item?.value || (water + sewer))
        const valueMap = { water, sewer, total }
        return +this.normalizeValue(valueMap[series]).toFixed(2)
      })
    },
    createConsumptionDataset(label, data, color, estimatedColor) {
      const isLine = this.selectedChartType === 'line'
      const isTotalMode = this.selectedConsumptionMode === 'total'

      return {
        label,
        data,
        backgroundColor: isLine
          ? (isTotalMode ? 'transparent' : this.createGradient())
          : this.activeData.map((item) => item && item.isEstimative ? estimatedColor : color),
        borderColor: color,
        borderWidth: 2,
        pointBackgroundColor: data.map(value => value > this.chartTargetValue ? '#FF7F00' : color),
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: isLine ? 5 : 0,
        pointHoverRadius: 8,
        fill: isLine && !isTotalMode,
        barThickness: 20,
        stack: 'consumption'
      }
    },
    initChart() {
      if (this.chart) {
        this.chart.destroy()
      }
      // Registrar os plugins personalizados
      Chart.plugins.register(targetLinePlugin)
      Chart.plugins.register(roundedBarsPlugin)
      const ctx = this.$refs.waterUsageChart.getContext('2d')
      this.chart = new Chart(ctx, {
        type: this.selectedChartType,
        data: this.chartData,
        options: this.chartOptions
      })
    },
    updateChart() {
      if (!this.chart) return
      
      // Se o tipo do gráfico mudou, precisamos destruir e recriar o gráfico
      if (this.chart.config.type !== this.selectedChartType) {
        this.chart.destroy()
        this.initChart()
        return
      }
      
      this.chart.data = this.chartData
      this.chart.options = this.chartOptions
      this.chart.update()
    }
  },
  watch: {
    startDate() {
      // Remoção do console.log
    },
    endDate() {
      // Remoção do console.log
    },
    selectedUnit() {
      this.updateChart()
    },
    selectedPeriod() {
      this.updateChart()
    },
    selectedChartType() {
      this.updateChart()
    },
    selectedConsumptionMode() {
      this.updateChart()
    },
    dailyData: {
      handler() {
        this.$nextTick(() => {
          if (this.chart) {
            this.updateChart()
          } else {
            this.initChart()
          }
        })
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart()
    })
  },
  beforeDestroy() {
    // Destruir a instância do gráfico para evitar vazamentos de memória
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
</script>

<style scoped>

.v-card {
  padding: 16px;
  flex-direction: column;
  display: flex;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
  border-radius: 10px; 
  gap: 10px;
}

.chart-container {
  width: 100%;
  height: 220px; 
  max-height: 100%;
  overflow: hidden;
  position: relative;
}

.chart-container canvas {
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
  max-height: 100%;
  display: block;
}

.chart-title{
  color: #1C1C1C;
  font-family: Manrope;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.toggle-group {
  height: 32px;
  border-radius: 24px;
  overflow: hidden;
  background-color: rgba(235, 245, 255, 0.7);
  box-shadow: none;
}

.toggle-group .v-btn {
  border-radius: 24px;
  height: 32px;
  min-width: 50px;
  font-size: 12px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  margin: 0 2px;
  background-color: transparent !important;
  color: #5B6A84 !important;
}

.toggle-group .v-btn.v-item--active {
  background-color: white !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

@media (max-width: 768px) {
  .toggle-container {
    margin-top: 8px;
  }
  
  .d-flex.justify-space-between {
    flex-direction: column;
    align-items: flex-start;
  }
}

.chart-date-info {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 4px;
  color: rgba(0, 0, 0, 0.70);
  font-family: Manrope;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  gap: 8px;
}

.date-info-icon {
  width: 18px;
  height: 18px;
  margin-right: 6px;
}

.chart-type-select {
  margin-left: 16px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(25, 118, 210, 0.08);
  font-family: Manrope, sans-serif;
  font-size: 15px;
  font-weight: 500;
  min-width: 180px;
  max-width: 220px;
}
</style> 
